import { Link, PressItem } from "@areahub/ui";
import { Box, ChakraProps, Text, useMediaQuery } from "@chakra-ui/react";
import Image from "next/image";
import { replaceAll } from "voca";

function getTypeDisplayName(type: string): string {
  return replaceAll(type, "_", " ");
}

export const PressItemCard = ({
  type,
  title,
  description,
  date,
  info,
  bg,
  logo,
  __typename,
  ...rest
}: Omit<PressItem, "__typename"> &
  ChakraProps & {
    link?: String;
    __typename?: "GCMS_PressItem";
  }) => {
  const [isLargerThanMd] = useMediaQuery("(min-width: 768px)");

  const stylingProps: ChakraProps = {
    bg: bg ? bg : "white",
    px: "4",
    py: "2",
    borderRadius: "10",
    boxShadow: "lg",
    h: isLargerThanMd ? "390px" : "auto",
    display: "flex",
    flexDirection: "column",
    sx: {
      _hover: {
        ".cardTitle": {
          textDecoration: "underline",
        },
      },
    },
    ...rest,
  };

  const typeColor = {
    press_release: "green.500",
    in_the_news: "red.500",
    white_paper: "orange.500",
    podcast: "blue.500",
    news_brief: "purple.500",
    blog: "#A853F7",
    "climate impact": "gray.500",
    Pollution: "#A854F7",
    Infrastructure: "#F87315",
    Product: "indigo.500",
    "Industrial Hazards": "#3B81F6",
    "Natural Hazards": "#21C55D",
  };
  const Encapsulation = getCardEncapsulation(info, __typename);

  return (
    <Encapsulation {...stylingProps}>
      {logo?.url && (
        <Box
          mx="auto"
          height={{
            base: "auto",
            lg: "165px",
          }}
        >
          <Box
            display={"flex"}
            alignItems={"center"}
            justifyContent={"center"}
            h={"full"}
            p={2}
          >
            <Image
              src={logo?.url as string}
              height={logo?.height}
              width={logo?.width}
              alt={logo?.alt ?? logo?.handle}
              placeholder={
                typeof logo?.blurDataURL === "string" ? "blur" : undefined
              }
              blurDataURL={
                typeof logo?.blurDataURL === "string"
                  ? logo?.blurDataURL
                  : undefined
              }
            />
          </Box>
        </Box>
      )}
      <Text
        fontSize={{
          base: "xs",
          lg: "sm",
        }}
        fontWeight="semibold"
        mb="1"
        bgColor={typeColor[type]}
        color="white"
        borderRadius={5}
        w="fit-content"
        px={2}
        py="1.5px"
        display="inline-block"
      >
        {getTypeDisplayName(type)}
      </Text>
      <Box
        fontSize={{
          base: "sm",
          lg: "md",
        }}
        className="cardTitle"
        fontWeight="bold"
      >
        <Text
          color={"black"}
          _hover={{
            color: "black",
          }}
        >
          {title}
        </Text>
      </Box>
      <Box
        fontSize={{
          base: "xs",
          lg: "sm",
        }}
        mt={{
          base: "2",
          md: "3",
        }}
        // Limit number of lines for the description
        noOfLines={{ base: 4, md: 2 }}
      >
        <Text
          color={"black"}
          _hover={{
            color: "black",
          }}
        >
          {description}
        </Text>
      </Box>
    </Encapsulation>
  );
};

function getCardEncapsulation(
  info?: PressItem["info"],
  __typename?: "GCMS_PressItem" | "GCMS_Post",
) {
  if (info) {
    return function LinkVersion(props: ChakraProps) {
      return <Link _hover={{ outline: "none" }} {...props} href={info.link} />;
    };
  }
  return function DefaultVersion(props: ChakraProps) {
    return <Box {...props} />;
  };
}
